import Prism from "prismjs";
import "prismjs/components/prism-python";
import "prismjs/components/prism-json";
import { useEffect, useMemo, useRef, useState, type UIEvent } from "react";

export type CodeLanguage = "python" | "javascript" | "json" | "rego" | "text";

// Rego (OPA policy language): Prism ships no grammar for it, so a small one here.
Prism.languages.rego ??= {
  comment: { pattern: /#.*/, greedy: true },
  string: { pattern: /"(?:\\.|[^"\\\n])*"|`[^`]*`/, greedy: true },
  keyword: /\b(?:package|import|default|if|else|contains|some|every|in|not|with|as|future|rego)\b/,
  boolean: /\b(?:true|false|null)\b/,
  number: /\b\d+(?:\.\d+)?\b/,
  function: /\b[a-z_][\w.]*(?=\()/i,
  operator: /:=|==|!=|<=|>=|[<>|&=+\-*/]/,
  punctuation: /[{}[\](),.;:]/,
};

export interface CodePart {
  text: string;
  /** Prism token type (keyword, string, comment, …); null for plain text. */
  type: string | null;
}

function flatten(tokens: (string | Prism.Token)[], type: string | null, out: CodePart[]): void {
  for (const t of tokens) {
    if (typeof t === "string") out.push({ text: t, type });
    else if (Array.isArray(t.content)) flatten(t.content as (string | Prism.Token)[], t.type, out);
    else if (typeof t.content === "string") out.push({ text: t.content, type: t.type });
    else flatten([t.content as Prism.Token], t.type, out);
  }
}

/** Code split into lines of coloured parts (Prism, MIT; ADR-0071). */
export function highlightLines(code: string, lang: CodeLanguage): CodePart[][] {
  const grammar = lang === "text" ? undefined : Prism.languages[lang];
  const parts: CodePart[] = [];
  if (grammar) flatten(Prism.tokenize(code, grammar), null, parts);
  else parts.push({ text: code, type: null });
  const lines: CodePart[][] = [[]];
  for (const p of parts) {
    const pieces = p.text.split("\n");
    pieces.forEach((piece, i) => {
      if (i > 0) lines.push([]);
      if (piece) (lines[lines.length - 1] as CodePart[]).push({ text: piece, type: p.type });
    });
  }
  return lines;
}

export interface CodeViewProps {
  code: string;
  language: CodeLanguage;
  /** Accessible name, e.g. the file name. */
  label: string;
  /** Edit in place (developers); the view stays read-only without it. */
  editable?: boolean;
  onChange?: (code: string) => void;
  maxHeight?: number;
  /** Number of the first line (an excerpt of a longer file). */
  firstLine?: number;
}

/**
 * A real code view: line numbers and syntax colouring, read-only by default. With `editable`
 * a transparent text area sits over the coloured code, so it stays coloured while typing.
 * Always left-to-right, in Arabic too.
 */
export function CodeView({ code, language, label, editable = false, onChange, maxHeight = 480, firstLine = 1 }: CodeViewProps) {
  const lines = useMemo(() => highlightLines(code, language), [code, language]);
  const pre = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState(false);
  useEffect(() => setFocus(false), [editable]);
  const sync = (e: UIEvent<HTMLTextAreaElement>) => {
    if (pre.current) {
      pre.current.scrollTop = e.currentTarget.scrollTop;
      pre.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };
  const body = (
    <div ref={pre} className="ap-code-lines" style={{ maxBlockSize: maxHeight }} aria-hidden={editable || undefined}>
      {lines.map((line, i) => (
        <div className="ap-code-line" key={i}>
          <span className="ap-code-no">{i + firstLine}</span>
          <span className="ap-code-text">
            {line.length ? line.map((p, j) => (
              <span key={j} className={p.type ? `ap-tok-${p.type}` : undefined}>
                {p.text}
              </span>
            )) : " "}
          </span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="ap-code" dir="ltr" data-testid="code-view" data-language={language} data-editing={editable || undefined} data-focus={focus || undefined}>
      {editable ? (
        <div className="ap-code-edit">
          {body}
          <textarea
            className="ap-code-input"
            aria-label={label}
            spellCheck={false}
            value={code}
            style={{ maxBlockSize: maxHeight }}
            onChange={(e) => onChange?.(e.target.value)}
            onScroll={sync}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            data-testid="code-edit"
          />
        </div>
      ) : (
        // A scrollable region must be reachable by keyboard (WCAG 2.1.1).
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        <div role="region" aria-label={label} tabIndex={0}>
          {body}
        </div>
      )}
    </div>
  );
}
